import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Eye, ChevronLeft, ChevronRight, ArrowRight, BookOpen, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import './PostDetailPage.css';

// Safe helper to access nested object properties without triggering Proxy errors
const safeGet = (obj, path, fallback = '') => {
  try {
    return path.split('.').reduce((o, key) => o?.[key], obj) || fallback;
  } catch {
    return fallback;
  }
};

export default function PostDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const galleryRef = useRef(null);

  const [post, setPost] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);

  // Helper for scrolling
  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = ref.current.offsetWidth;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const fetchPostData = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/data.json');
      const allPosts = await response.json();
      
      const foundPost = allPosts.find(p => p.slug === slug);
      
      if (foundPost) {
        setPost(foundPost);
        
        const contentPath = foundPost.content_paths[currentLang] || foundPost.content_paths['en'];
        const htmlResponse = await fetch(contentPath);
        const htmlText = await htmlResponse.text();
        setHtmlContent(htmlText);
      }
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  }, [slug, currentLang]);

  useEffect(() => {
    if (slug) fetchPostData();
    window.scrollTo(0, 0);
  }, [slug, fetchPostData]);

  // SAFE image error handler
  useEffect(() => {
    if (!htmlContent) return;
    
    let images = [];
    
    const timeoutId = setTimeout(() => {
      try {
        images = Array.from(document.querySelectorAll('.wine-article img'));
        
        images.forEach(img => {
          // Use a named function for easier removal
          function fallbackHandler() {
            // Prevent infinite loop
            if (!img.src.includes('default-wine')) {
              console.log('Image failed to load, using fallback');
              img.src = '/images/default-wine.jpg';
              img.onerror = null;
            }
          }
          
          // Remove existing handler and add new one
          img.removeEventListener('error', fallbackHandler);
          img.addEventListener('error', fallbackHandler);
          
          // Store for cleanup
          img._fallbackHandler = fallbackHandler;
        });
      } catch (e) {
        console.log('Image handler error:', e);
      }
    }, 200);
    
    return () => {
      clearTimeout(timeoutId);
      images.forEach(img => {
        if (img._fallbackHandler) {
          img.removeEventListener('error', img._fallbackHandler);
          delete img._fallbackHandler;
        }
      });
    };
  }, [htmlContent]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(currentLang === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Safe memoized values using safeGet
  const displayTitle = useMemo(() => {
    if (!post) return '';
    return safeGet(post, currentLang === 'zh' ? 'title.zh' : 'title.en', 'Article');
  }, [post, currentLang]);

  const displayExcerpt = useMemo(() => {
    if (!post) return '';
    const excerpt = safeGet(post, currentLang === 'zh' ? 'excerpt.zh' : 'excerpt.en', '');
    const summary = safeGet(post, currentLang === 'zh' ? 'summary.zh' : 'summary.en', '');
    return excerpt || summary || displayTitle;
  }, [post, currentLang, displayTitle]);

  const displayCategory = useMemo(() => {
    if (!post?.category) return '';
    return safeGet(post, 'category.name.' + currentLang, '');
  }, [post, currentLang]);

  // ULTRA-SAFE formatted content - minimal processing, no complex regex
  const formattedContent = useMemo(() => {
    if (!htmlContent) return '';
    
    // Only do the absolute minimum processing
    let processed = htmlContent;
    
    // 1. Add loading="lazy" to all images (simple string replace)
    processed = processed.replace(/<img /g, '<img loading="lazy" ');
    
    // 2. Handle align="center" (simple attribute replacement)
    processed = processed.replace(/align="center"/g, 'style="text-align:center;"');
    
    // 3. Convert center tags to divs
    processed = processed.replace(/<center>/g, '<div style="text-align:center;">');
    processed = processed.replace(/<\/center>/g, '</div>');
    
    // 4. Add basic responsive style to images if not present
    // Only do this simple check - no complex lookarounds
    if (!processed.includes('max-width:')) {
      // Simple replacement for img tags without complex logic
      processed = processed.replace(/<img /g, '<img style="max-width:100%;height:auto;" ');
    }
    
    // 5. Add alt text only if absolutely needed (simple check)
    if (!processed.includes('alt=')) {
      const safeAlt = currentLang === 'zh' ? '葡萄酒图片' : 'Wine image';
      processed = processed.replace(/<img /g, `<img alt="${safeAlt}" `);
    }
    
    return processed;
  }, [htmlContent, currentLang]); // REMOVED 'post' dependency!

  // 加载状态 - 在 Hooks 之后返回
  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center font-serif italic text-[#556B2F]">Loading...</div>;
  }
  
  if (!post) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-3xl text-[#556B2F]">Post not found.</div>;
  }

  return (
    <main className="bg-cream min-h-screen font-sans flex flex-col">
      <SEO title={displayTitle} />
      
      <div className="h-24 md:h-32 w-full bg-cream" aria-hidden="true" />

      {/* Cover Image Section */}
      <section className="w-full relative bg-black overflow-hidden shadow-sm aspect-[16/10] md:aspect-[21/9] lg:max-h-[600px]">
        {post.cover_image && (
          <img 
            src={post.cover_image} 
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" 
            alt={displayTitle}
            itemProp="image"
            loading="eager"
          />
        )}
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 mb-24 mt-16 w-full">
        <nav className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#556B2F] font-bold tracking-[0.2em] text-[10px] hover:text-[#800020] transition-colors uppercase">
            <ChevronLeft size={14} /> {t('post_detail.back_home')}
          </Link>
        </nav>

        {/* Article with Schema.org markup */}
        <article 
          className="bg-white rounded-[2.5rem] shadow-sm border border-[#556B2F]/5 overflow-hidden mb-16"
          itemScope
          itemType="https://schema.org/BlogPosting"
        >
          {/* Hidden metadata for search engines */}
          <meta itemProp="headline" content={displayTitle} />
          <meta itemProp="description" content={displayExcerpt} />
          <meta itemProp="datePublished" content={post.date} />
          <meta itemProp="dateModified" content={post.updated_at || post.date} />
          <meta itemProp="author" content="Euro Wine Trip" />
          <meta itemProp="publisher" content="Euro Wine Trip" />
          <meta itemProp="image" content={post.cover_image || ''} />
          {post.category && (
            <meta itemProp="articleSection" content={displayCategory} />
          )}
          {post.keywords && (
            <meta itemProp="keywords" content={Array.isArray(post.keywords) ? post.keywords.join(', ') : post.keywords} />
          )}
          
          {post.word_count && (
            <meta itemProp="wordCount" content={post.word_count.toString()} />
          )}

          <div className="p-8 md:p-20">
            <header className="mb-12">
              <h1 className="text-2xl md:text-4xl font-serif italic text-[#3C4128] leading-[1.2] mb-10" itemProp="name">
                {displayTitle}
              </h1>
              <div className="flex flex-wrap items-center gap-8 text-[11px] font-bold uppercase tracking-[0.2em] text-[#556B2F]/40 border-b border-[#556B2F]/10 pb-8">
                <time 
                  className="flex items-center gap-2"
                  dateTime={post.date}
                  itemProp="datePublished"
                >
                  <Calendar size={14} className="text-[#556B2F]" />
                  {formatDate(post.date)}
                </time>
                <span className="flex items-center gap-2">
                  <Eye size={14} className="text-[#556B2F]" />
                  {post.view_count || 0} {t('post_detail.views')}
                </span>
                {displayCategory && (
                  <span className="flex items-center gap-2" itemProp="articleSection">
                    <BookOpen size={14} className="text-[#556B2F]" />
                    {displayCategory}
                  </span>
                )}
              </div>
            </header>

            {/* Main Content from HTML File */}
            <section 
              className="wine-article mb-12" 
              dangerouslySetInnerHTML={{ __html: formattedContent }}
              itemProp="articleBody"
            />
            
            <div className="text-[11px] text-[#556B2F]/50 italic font-serif mt-8 pb-4 border-b border-[#556B2F]/5 text-center">
              {currentLang === 'zh' 
                ? "注：本文图文内容均由 AI 辅助创作，经人工审核发布。" 
                : "Note: This article is AI-assisted and human-reviewed."}
            </div>

            {/* Gallery Section */}
            {post.gallery && post.gallery.length > 0 && (
              <section className="mt-12 border-t border-[#556B2F]/5 pt-12">
                <h2 className="text-lg font-bold text-[#556B2F] mb-8 flex items-center gap-3">
                  <ImageIcon size={20} />
                  {currentLang === 'zh' ? '图集' : 'Gallery'}
                </h2>
                <div className="relative group">
                  <div 
                    ref={galleryRef}
                    className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar"
                  >
                    {post.gallery.map((img, index) => (
                      <div key={index} className="min-w-full md:min-w-[calc(50%-0.5rem)] snap-start rounded-2xl overflow-hidden aspect-[4/3]">
                        <img 
                          src={img.url} 
                          alt={img.alt?.[currentLang] || `${displayTitle} - ${t('post_detail.gallery_image')} ${index + 1}`} 
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation buttons */}
                  {post.gallery.length > 1 && (
                    <>
                      <button 
                        onClick={() => scroll(galleryRef, 'left')}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                        aria-label={t('common.previous')}
                      >
                        <ChevronLeft size={20} className="text-[#556B2F]" />
                      </button>
                      <button 
                        onClick={() => scroll(galleryRef, 'right')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                        aria-label={t('common.next')}
                      >
                        <ChevronRight size={20} className="text-[#556B2F]" />
                      </button>
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </article>

        {/* Related Posts Section */}
        {post.related && post.related.length > 0 && (
          <section className="mb-12">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-[#556B2F]/5">
              <h2 className="text-xl font-bold text-[#556B2F] flex items-center gap-4 mb-8">
                <BookOpen size={24} />
                {currentLang === 'zh' ? '延伸阅读' : 'Further Reading'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {post.related.map((rel) => (
                  <article key={rel.slug} className="bg-cream/10 rounded-3xl p-6 border border-[#556B2F]/5">
                    <h3 className="text-lg font-serif italic mb-3">
                      <Link to={`/post/${rel.slug}`} className="hover:text-[#800020] transition-colors line-clamp-2">
                        {rel.title[currentLang]}
                      </Link>
                    </h3>
                    <Link 
                      to={`/post/${rel.slug}`} 
                      className="inline-flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-[#556B2F] hover:gap-4 transition-all"
                    >
                      {currentLang === 'zh' ? '阅读更多' : 'Read More'} <ArrowRight size={12} />
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}